import React, { useState } from 'react';
import { XMarkIcon, CheckIcon, UserPlusIcon, PencilSquareIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import ResidentSearchModal from './ResidentSearchModal';

const FamilyMemberFormModal = ({
  show,
  onClose,
  familyMember,
  onChange,
  onSave,
  onCancel,
  isEditing = false,
  currentHouseholdHeadId = null
}) => {
  const [showSearchModal, setShowSearchModal] = useState(false);

  if (!show) return null;


  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    const validationErrors = [];

    if (!familyMember.first_name?.trim() && !familyMember.name?.trim()) {
      validationErrors.push('First Name is required');
    }

    if (!familyMember.last_name?.trim() && !familyMember.name?.trim()) {
      validationErrors.push('Last Name is required');
    }

    if (!familyMember.relationship_to_head?.trim() && !familyMember.relationship?.trim()) {
      validationErrors.push('Relationship to Head is required');
    }

    if (!familyMember.gender?.trim()) {
      validationErrors.push('Gender is required');
    }

    if (!familyMember.birthdate?.trim()) {
      validationErrors.push('Birthdate is required');
    }

    if (!familyMember.birthplace?.trim()) {
      validationErrors.push('Birthplace is required');
    }

    if (validationErrors.length > 0) {
      alert('Please fix the following errors:\n' + validationErrors.join('\n'));
      return;
    }

    onSave();
  };

  const handleResidentSelect = (selectedResident) => {
    console.log('handleResidentSelect called with:', selectedResident);
    console.log('Current familyMember state:', familyMember);

    // Auto-populate form with selected resident data
    const updatedMember = {
      ...familyMember,
      name: selectedResident.name,
      first_name: selectedResident.first_name,
      middle_name: selectedResident.middle_name,
      last_name: selectedResident.last_name,
      age: selectedResident.age,
      birthdate: selectedResident.birthdate,
      birthplace: selectedResident.birthplace || '',
      gender: selectedResident.gender,
      occupation: selectedResident.occupation || '',
      // Keep existing relationship if already set, otherwise empty for user to select
      relationship: familyMember.relationship || '',
      relationship_to_head: familyMember.relationship_to_head || '',
      // Additional fields from selected resident
      existing_resident_id: selectedResident.id,
      barangay_id_number: selectedResident.barangay_id_number,
      contact_number: selectedResident.contact_number
    };

    console.log('Updated member data:', updatedMember);
    onChange(updatedMember);
    setShowSearchModal(false);

    // Show success notification
    toast.success(`${selectedResident.name} has been selected. Please set their relationship.`, {
      duration: 4000
    });
  };

  const handleInputChange = (field, value) => {
    const updatedMember = {
      ...familyMember,
      [field]: value
    };

    onChange(updatedMember);
  };

  const relationshipOptions = [
    'Head',
    'Spouse',
    'Son',
    'Daughter',
    'Father',
    'Mother',
    'Brother',
    'Sister',
    'Grandfather',
    'Grandmother',
    'Uncle',
    'Aunt',
    'Cousin',
    'Nephew',
    'Niece',
    'In-law',
    'Other'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEditing ? 'Edit Family Member' : 'Add Family Member'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Search Section - Only show when adding new family member */}
          {!isEditing && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-blue-900">Quick Add</h4>
                  <p className="text-xs text-blue-700 mt-1">
                    Search for existing residents to add as family members.
                    {familyMember.existing_resident_id && (
                      <span className="block text-green-700 font-medium mt-1">
                        ✓ Resident selected: {familyMember.name || `${familyMember.first_name || ''} ${familyMember.last_name || ''}`.trim()}. Set relationship below.
                      </span>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSearchModal(true)}
                  className="inline-flex items-center px-3 py-2 border border-blue-300 shadow-sm text-sm leading-4 font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                  {familyMember.existing_resident_id ? 'Change Selection' : 'Search Residents'}
                </button>
              </div>
            </div>
          )}

          {/* Form Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Name Fields Row */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={familyMember.first_name || ''}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter first name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Middle Name
              </label>
              <input
                type="text"
                value={familyMember.middle_name || ''}
                onChange={(e) => handleInputChange('middle_name', e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter middle name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={familyMember.last_name || ''}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter last name"
                required
              />
            </div>

            {/* Personal Information Row */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Relationship to Head <span className="text-red-500">*</span>
              </label>
              <select
                value={familyMember.relationship_to_head || familyMember.relationship || ''}
                onChange={(e) => {
                  const newValue = e.target.value;
                  const updatedMember = {
                    ...familyMember,
                    relationship_to_head: newValue,
                    relationship: newValue // Keep both fields in sync
                  };

                  onChange(updatedMember);
                }}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select relationship</option>
                {relationshipOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gender <span className="text-red-500">*</span>
              </label>
              <select
                value={familyMember.gender || ''}
                onChange={(e) => handleInputChange('gender', e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Age
              </label>
              <input
                type="number"
                value={familyMember.age || ''}
                onChange={(e) => handleInputChange('age', e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter age"
                min="0"
                max="150"
              />
            </div>

            {/* Date and Location Row */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Birthdate <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={familyMember.birthdate || ''}
                onChange={(e) => handleInputChange('birthdate', e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Birthplace <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={familyMember.birthplace || ''}
                onChange={(e) => handleInputChange('birthplace', e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter birthplace"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Occupation
              </label>
              <input
                type="text"
                value={familyMember.occupation || ''}
                onChange={(e) => handleInputChange('occupation', e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter occupation"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {isEditing ? (
                <>
                  <PencilSquareIcon className="h-4 w-4 mr-2" />
                  Update Member
                </>
              ) : (
                <>
                  <UserPlusIcon className="h-4 w-4 mr-2" />
                  Add Member
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Resident Search Modal */}
      <ResidentSearchModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        onSelect={handleResidentSelect}
        excludeHouseholdId={currentHouseholdHeadId}
        title="Search Existing Residents"
      />
    </div>
  );
};

export default FamilyMemberFormModal;
